import { createSlice } from '@reduxjs/toolkit'
import { getAdjacencyMap } from 'store/helper';
import gameConstants from 'constants/game';
import misc from 'misc';

const { areas } = gameConstants;

const initialGameState = {
  guesses: undefined,
  check: undefined,
  cursor: {
    position: undefined,
    isAcross: true
  },
};
const initialState = {
  gameData: undefined,
  gameState: initialGameState,
  saves: {},
  directory: "",
};

export const slice = createSlice({
  name: 'reducer',
  initialState,
  reducers: {
    clear: (state, action) => {
      if (action.payload.area === areas.board) {
        state.gameState.guesses = Array(state.gameData.grid.length).fill(' ');
        state.gameState.check = Array(state.gameData.grid.length).fill(false);
      } else if (state.gameState.cursor.position === undefined) {
        return state;
      } else if (action.payload.area === areas.letter) {
        state.gameState.guesses[state.gameState.cursor.position] = ' ';
        state.gameState.check[state.gameState.cursor.position] = false;
      } else {
         console.log('todo: clear answer');
         return state
      }
    },
    solve: (state, action) => {
      if (action.payload.area === areas.board) {
        state.gameState.guesses = state.gameData.grid;
        state.gameState.check = Array(state.gameData.grid.length).fill(false);
      } else if (state.gameState.cursor.position === undefined) {
        return state;
      } else if (action.payload.area === areas.letter) {
        state.gameState.guesses[state.gameState.cursor.position] = state.gameData.grid[state.gameState.cursor.position];
        state.gameState.check[state.gameState.cursor.position] = false;
      } else {
        console.log('todo: solve answer')
        return state;
      }
    },
    check: (state, action) => {
      if (action.payload.area === areas.board) {
        state.gameState.check = Array(state.gameData.grid.length).fill(true);
      } else if (!state.gameState.cursor.position) {
        return state;
      } else if (action.payload.area === areas.letter) {
        state.gameState.check[state.gameState.cursor.position] = true;
      } else {
        console.log('todo: check answer');
        return state;
      }
    },
    upsertSaves: (state, { payload: { saves }}) => {
      state.saves = {
        ...state.saves,
        ...saves
      };
    },
    updateDirectory: (state, { payload: { directory }}) => ({
      ...state,
      directory,
    }),
    fetchGameStart: (state, { payload: { gameId }}) => ({
      ...state,
      gameData: undefined,
      gameState: initialGameState,
      gameId,
      isLoading: true,
    }),
    initGame: (state, { payload: { gameData, gameState, gameId }}) => {
      state.gameData = {
        ...gameData,
        author: misc.decode(gameData.author),
        title: misc.decode(gameData.title),
        clues: {
          across: gameData.clues.across.map(misc.decode),
          down: gameData.clues.down.map(misc.decode),
        },
        moves: getAdjacencyMap(gameData),
      };
      state.gameState = {
        ...initialGameState,
        check: Array(gameData.grid.length).fill(false),
        guesses: gameState
          ? gameState.guesses
          : Array(gameData.grid.length).fill(' '),
      }
      state.gameId = gameId;
    },
    fetchGameSuccess: (state, { payload: { gameId }}) => ({
      ...state,
      gameId,
      isLoading: false,
    }),
    fetchGameFailed: (state, { payload: { gameId }}) => ({
      ...state,
      gameData: undefined,
      gameState: initialGameState,
      gameId,
      isLoading: false,
    }),
    unselect: (state) => {
      state.gameState.cursor = initialGameState.cursor;
    },
    select: (state, { payload: { position, isAcross }}) => {
      const lastPosition = state.gameState.cursor.position;
      if (state.gameData.grid[position] === '.') return state;

      if (position === lastPosition) {
        state.gameState.cursor.isAcross = !state.gameState.cursor.isAcross;
      } else {
        state.gameState.cursor = { ...state.gameState.cursor, position };
      }
      if (isAcross !== undefined) {
        state.gameState.cursor.isAcross = isAcross;
      }
    },
    nextCell: (state) => {
      const { position, isAcross } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].cells.next[isAcross ? 0 : 1];
    },
    nextClue: (state) => {
      const { position, isAcross } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].clues.next[isAcross ? 0 : 1];
    },
    priorCell: (state, { payload: { destructive }}) => {
      const { position, isAcross } = state.gameState.cursor;
      if (position === undefined) return state;
      if (!destructive || state.gameState.guesses[position] === ' ') {
        state.gameState.cursor.position = state.gameData?.moves[position].cells.prior[isAcross ? 0 : 1];
      } else if (destructive) {
        state.gameState.guesses[position] = ' ';
      }
    },
    priorClue: (state) => {
      const { position, isAcross } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].clues.prior[isAcross ? 0 : 1];
    },
    toggleDirection: (state) => {
      state.gameState.cursor.isAcross = !state.gameState.cursor.isAcross
    },
    shiftUp: (state) => {
      const { position } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].cells.up;
    },
    shiftDown: (state) => {
      const { position } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].cells.down;
    },
    shiftLeft: (state) => {
      const { position } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].cells.left;
    },
    shiftRight: (state) => {
      const { position } = state.gameState.cursor;
      if (position === undefined) return state;
      state.gameState.cursor.position = state.gameData?.moves[position].cells.right;
    },
    inputLetter: (state, { payload: { key }}) => {
      if (state.gameState.cursor.position === undefined) return state;
      state.gameState.guesses[state.gameState.cursor.position] = key.toUpperCase();
      state.gameState.check[state.gameState.cursor.position] = false;
    },
  }
});

export const {
  clear,
  solve,
  check,
  upsertSaves,
  updateDirectory,
  fetchGameStart,
  initGame,
  fetchGameSuccess,
  fetchGameFailed,
  priorClue,
  priorCell,
  select,
  nextCell,
  nextClue,
  unselect,
  toggleDirection,
  inputLetter,
  shiftUp,
  shiftDown,
  shiftLeft,
  shiftRight,
} = slice.actions

export default slice.reducer
